import { ARIA_LABELS, BUTTON_LABELS } from '@/constants';
import { loadShareScorecardWithManager } from '@/services';
import { useCallback, useEffect, useState } from 'react';
import { useGlobalStore } from '@/store';
import type { ShareWithManagerButtonProps } from '@/components/Buttons/ShareWithManagerButton/types';
import type { TShareWithManagerButtonState } from '@/components/Buttons/ShareWithManagerButton/types';

// ================================
// Type Definitions
// ================================

/**
 * ShareButtonContent represents the visual content displayed in the button
 * based on the current sharing state. It includes an optional icon name
 * and a label string that will be shown to the user.
 */
type ShareButtonContent = {
  iconName?: 'CheckCircle' | 'WarningCircle';
  label: string;
};

/**
 * UseShareWithManagerFeatureProps defines the props accepted by the useShareWithManagerFeature hook.
 * These props are a subset of ShareWithManagerButtonProps and include:
 * - disabled: Whether the share button should be disabled
 * - initialState: The initial state of the share button (defaults to 'initial')
 * - onShare: Optional callback to execute after sharing
 * - scoreCardId: The unique identifier of the scorecard to share
 * - userId: The unique identifier of the user performing the share
 * - sessionId: The unique identifier of the session associated with the scorecard
 */
type UseShareWithManagerFeatureProps = Pick<
  ShareWithManagerButtonProps,
  'disabled' | 'initialState' | 'onShare' | 'scoreCardId' | 'userId' | 'sessionId'
>;

// ================================
// Hook Definition
// ================================

/**
 * useShareWithManagerFeature Hook
 *
 * This custom hook manages the logic for sharing a scorecard with a manager.
 * It handles state management for the share button, validates required parameters,
 * makes the API request to share the scorecard, updates the global store, and
 * provides methods to get the appropriate button content and ARIA labels based
 * on the current sharing state.
 */
export const useShareWithManagerFeature = ({
  disabled = false,
  initialState = 'initial',
  onShare,
  scoreCardId,
  sessionId,
  userId,
}: UseShareWithManagerFeatureProps) => {
  // ================================
  // Store & State Management
  // ================================

  // Get the global store function to update scorecard data after sharing
  const { updateScorecard } = useGlobalStore();

  // Track the current state of the share button/operation:
  // 'initial' - Default state, ready to share
  // 'sharing' - Currently sending request to server
  // 'shared' - Successfully shared with the manager
  // 'error' - An error occurred during the share operation
  const [shareState, setShareState] = useState<TShareWithManagerButtonState>(initialState);

  // Keep local state aligned when parent code drives a different initialState
  // after the component has already mounted.
  useEffect(() => {
    setShareState(initialState);
  }, [initialState]);

  // ================================
  // Event Handlers
  // ================================

  /**
   * handleClick - Asynchronous handler triggered when the share button is clicked.
   *
   * This function:
   * 1. Validates that the button is not disabled and is in the initial state
   * 2. Validates that required parameters (userId and scoreCardId) are provided
   * 3. Calls the scoring service to send the share request
   * 4. Executes the optional onShare callback after a successful response
   * 5. Updates the global store to reflect the shared state
   * 6. Manages error states and displays error messages
   *
   * The function uses useCallback to maintain stable reference and prevent unnecessary re-renders.
   */
  const handleClick = useCallback(async () => {
    // Early return: Prevent action if the button is disabled or not in the initial state
    // This prevents multiple rapid clicks or sharing while already in progress
    if (disabled || shareState !== 'initial') return;

    // Validate required parameters for sharing
    // Both userId and scoreCardId are essential to identify what is being shared
    // and who is performing the action
    if (!userId || !scoreCardId || !sessionId) {
      setShareState('error');
      console.error(
        `Missing params for sharing: userId: ${userId} & scoreCardId: ${scoreCardId} & sessionId: ${sessionId}`,
      );

      return setTimeout(() => setShareState('initial'), 2000); // Reset to initial after showing error
    }

    // Temporary variable to hold the interval ID for clearing later
    // Note: This is declared but not actively used in the current implementation
    // It was likely left for potential future use or logging functionality
    let intervalId: NodeJS.Timeout | null = null;

    try {
      // Transition to 'sharing' state to show visual feedback to the user
      // This typically displays a loading indicator and disables further interactions
      setShareState('sharing');

      // Execute the API call to share the scorecard with the manager
      // This sends a POST request with the userId, scoreCardId, and sessionId to the backend
      const result = await loadShareScorecardWithManager({ userId, scoreCardId, sessionId });

      // Execute the optional onShare callback if provided by the parent component
      // This allows parent components to perform additional actions after sharing
      // (e.g., analytics tracking, notifications, UI updates)
      if (onShare) {
        await onShare();
      }

      // Handle successful share operation
      // Only update state if the API response is truthy (successful)
      if (result) {
        // Brief pause so the sharing animation is visible before transitioning to 'shared'
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Transition to 'shared' state to show success message with green styling
        setShareState('shared');

        /**
         * Update the scorecard in the global store to reflect that it has been
         * shared with the manager. This ensures the UI state stays synchronized
         * with the backend. This is particularly important for maintaining
         * consistency if a user:
         * - Shares a scorecard from one view/page
         * - Then navigates to another view showing the same scorecard
         * - Before the page is refreshed
         *
         * Without this update, stale data could be displayed until a page reload.
         */
        updateScorecard(scoreCardId, { sharedWithManager: true });

        // Clear interval on success (if one was set)
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    } catch (error) {
      // Handle errors that occur during the share operation
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Clear interval on error as well to ensure cleanup
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }

      // Log the error for debugging and monitoring purposes
      console.error('Error sharing with manager:', error);

      // Set the state to 'error' to display an error message to the user
      setShareState('error');
    }
  }, [disabled, shareState, onShare, userId, scoreCardId, sessionId, updateScorecard]);

  // ================================
  // Helper Functions
  // ================================

  /**
   * getButtonContent - Determines the button's visual content (label and icon)
   * based on the current share state.
   *
   * Returns appropriate text and icon combinations for each state:
   * - 'sharing': Shows "Sharing with manager..." with a check circle icon
   * - 'shared': Shows "Shared with manager!" with a check circle icon
   * - 'error': Shows "Error" with a warning circle icon
   * - 'initial': Shows "Share with manager" with no icon (default)
   *
   * @returns {ShareButtonContent} Object containing the label and optional iconName
   */
  const getButtonContent = (): ShareButtonContent => {
    switch (shareState) {
      case 'sharing':
        return {
          iconName: 'CheckCircle',
          label: BUTTON_LABELS.SHARING_WITH_MANAGER,
        };

      case 'shared':
        return {
          iconName: 'CheckCircle',
          label: BUTTON_LABELS.SHARED_WITH_MANAGER,
        };

      case 'error':
        return {
          iconName: 'WarningCircle',
          label: BUTTON_LABELS.ERROR,
        };

      case 'initial':
      default:
        return {
          label: BUTTON_LABELS.SHARE_WITH_MANAGER,
        };
    }
  };

  /**
   * getAriaLabel - Returns the appropriate ARIA label for the button
   * based on the current share state. This ensures proper accessibility
   * for screen readers and assistive technologies.
   *
   * ARIA labels provide descriptive text that screen readers announce
   * to users with visual impairments, describing the current state and
   * function of the share button.
   *
   * @returns {string} The ARIA label corresponding to the current state
   */
  const getAriaLabel = () => {
    switch (shareState) {
      case 'sharing':
        return ARIA_LABELS.SHARING_WITH_MANAGER;

      case 'shared':
        return ARIA_LABELS.SHARED_WITH_MANAGER;

      case 'error':
        return ARIA_LABELS.ERROR;

      case 'initial':
      default:
        return ARIA_LABELS.SHARE_WITH_MANAGER;
    }
  };

  // ================================
  // Return Value
  // ================================

  /**
   * Return all necessary values and functions for the component using this hook.
   *
   * Properties:
   * - getAriaLabel: Function to get the current ARIA label based on state
   * - getButtonContent: Function to get the current button content (label + icon)
   * - handleClick: Function to handle the share button click event
   * - shareState: The current state of the share operation
   */
  return {
    getAriaLabel,
    getButtonContent,
    handleClick,
    shareState,
  };
};
